import { convertToLocalDateTime } from "components/helpers";

import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import TagList from "components/TagList";
import { SELECT_COLUMN_ID } from "constants/Constants";
import { ScrollColumn } from "components/BaseTableV3/ScrollColumn";
import { TableLink } from "components/BaseTableV3/TableLink";
import DepartmentService from "services/Department";
import EntityService from "services/Entity";
import { EPOCH_TIMESTAMP } from "constants/Constants";
import RuvixxDate from "components/RuvixxDate";
import ACL_RELATIONSHIPS from "acl-relationships";
import { CampaignContactTableActions } from "./CampaignContactTableActions";
import { isContactCallable } from "helpers/contacts";
import CampaignStatusModal from "routes/campaign/components/CampaignStatusModal";
import { isCampaignTargetContact } from "./CampaignContactsTableV3";
import { NULLABLE_OPERATORS } from "components/NewAdvancedSearch/filters/selectOperators";

// Prefer optimistic map entry; fall back to row data so a stale (empty) map after a cache hit doesn't disable every row.
const getIsIncluded = (includedInCampaign, contact, campaignId) => {
  if (!contact) return false;
  const override = includedInCampaign[contact.id];
  if (override !== undefined) return override;
  return isCampaignTargetContact(contact, campaignId);
};

// Evaluate these reactively via useAuthorized in the consuming hook rather than
// at module load: this module is eagerly imported at app boot, so a top-level
// checkIsAuthorized() would run before userAuth is in the store on a cold
// session and freeze the result as false, disabling the toggle until a full
// page reload.
export const CAN_SET_CAMPAIGN_TARGET_PERMISSIONS = [
  ACL_RELATIONSHIPS.campaignTargetContacts.create,
  ACL_RELATIONSHIPS.campaignTargetContacts.delete,
];

export const CAN_CALL_PERMISSIONS = [
  ACL_RELATIONSHIPS.dialSession.read,
  ACL_RELATIONSHIPS.dialSessionPrivate.create,
  ACL_RELATIONSHIPS.callQueueNextContact.read,
  ACL_RELATIONSHIPS.callQueueAddContact.create,
  ACL_RELATIONSHIPS.disposition.read,
  ACL_RELATIONSHIPS.emailUrlForms.read,
  ACL_RELATIONSHIPS.voicemailForFilters.read,
  ACL_RELATIONSHIPS.twilioToken.read,
  ACL_RELATIONSHIPS.tag.read,
  ACL_RELATIONSHIPS.user.read,
  ACL_RELATIONSHIPS.campaignStatuses.read,
  ACL_RELATIONSHIPS.twilioDisposition.create,
  ACL_RELATIONSHIPS.twilioAddToCall.create,
  ACL_RELATIONSHIPS.twilioRemoveFromCall.create,
  ACL_RELATIONSHIPS.twilioUpdateParticipant.create,
];

export const CAN_EDIT_STATUS_PERMISSIONS = [
  ACL_RELATIONSHIPS.campaignTargetChangeStatus.edit,
];

export const getCampaignContactsTableColumns = ({
  entityId = null,
  campaignId = null,
  activeSession = null,
  includedInCampaign = {},
  handleAddOrRemoveCampaignContact,
  invalidateData,
  canSetCampaignTarget = false,
  canCall = false,
  canEditStatus = false,
}) => {
  return [
    {
      id: SELECT_COLUMN_ID,
      size: 60,
      enableSorting: false,
      meta: {
        isDisplay: true,
      },
      header: ({ table }) => (
        <Checkbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onCheckedChange: checked => table.toggleAllRowsSelected(!!checked),
            "aria-label": "Select all rows",
          }}
        />
      ),
      cell: ({ row }) => (
        <div>
          <Checkbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected() && !row.getIsSelected(),
              onCheckedChange: checked => row.toggleSelected(!!checked),
              "aria-label": "Select row",
            }}
          />
        </div>
      ),
    },
    {
      header: "Tags",
      id: "Tags",
      accessorKey: "tags",
      enableSorting: false,
      cell: ({ row }) => (
        <TagList
          tags={row.getValue("Tags")}
          modelType="Contact"
          modelId={row.original?.id}
          tableCell
          onUpdate={invalidateData}
        />
      ),
    },
    {
      header: "Contact ID",
      id: "Contact ID",
      accessorKey: "contact_id",
      cell: ({ row }) => (
        <TableLink href={`/contacts/${row.original?.id}`}>
          {row.original?.id}
        </TableLink>
      ),
    },
    {
      header: "Name",
      id: "Name",
      accessorKey: "full_name",
      cell: ({ row }) => (
        <TableLink href={`/contacts/${row.original.id}`}>
          {row.getValue("Name")}
        </TableLink>
      ),
    },
    {
      header: "Last Voicemail",
      id: "Last Voicemail",
      accessorKey: "last_voicemail",
      cell: ({ row }) => {
        const value = row.getValue("Last Voicemail");
        return value === EPOCH_TIMESTAMP || !value ? null : (
          <RuvixxDate date={value} />
        );
      },
    },
    {
      header: "Alternate Names",
      id: "Alternate Names",
      accessorKey: "alternate_names",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {(row.original?.alternate_names ?? []).map(({ id, name }) => (
            <li key={id}>{name}</li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Email",
      id: "Email",
      accessorKey: "email",
    },

    {
      header: "Department",
      id: "Department",
      accessorKey: "department_name",
    },
    {
      header: "Alternate Emails",
      id: "Alternate Emails",
      accessorKey: "email_addresses",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {(row.original?.email_addresses ?? []).map(({ id, email }) => (
            <li key={id}>{email}</li>
          ))}
        </ScrollColumn>
      ),
    },

    ...(!entityId
      ? [
          {
            header: "Entity",
            id: "Entity",
            accessorKey: "entity_name",
            cell: ({ row }) => (
              <TableLink href={`/entities/${row.original?.entity_id}`}>
                {row.getValue("Entity")}
              </TableLink>
            ),
          },
        ]
      : []),

    {
      header: "Title",
      id: "Title",
      accessorKey: "title",
    },

    {
      header: "Phone Numbers",
      id: "Phone Numbers",
      accessorKey: "phone_numbers",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row.original.phone_numbers?.map(phone => (
            <li key={phone.id}>
              {phone.ext ? `${phone.number}x${phone.ext}` : phone.number}
            </li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Effective Date",
      id: "Effective Date",
      accessorKey: "effective_date",
      enableSorting: false,
      cell: ({ row }) => {
        const contact = row.original;

        if (!contact || !campaignId || !contact.campaign_targets) return "";

        const campaignTarget = contact.campaign_targets.filter(
          c => c.campaign_id === campaignId
        )[0];
        return campaignTarget?.effective_date || "";
      },
    },
    {
      header: "Status Last Updated",
      id: "Status Last Updated",
      accessorKey: "status_last_updated_at",
      cell: ({ row }) => {
        const value = row.getValue("Status Last Updated");
        if (value !== undefined && value !== null) {
          return convertToLocalDateTime(value).format("YYYY-MM-DD");
        }
        return "N/A";
      },
    },
    {
      header: "Last Activity Date",
      id: "Last Activity Date",
      accessorKey: "last_activity_date",
      cell: ({ row }) => (
        <div>
          {row.getValue("Last Activity Date")
            ? convertToLocalDateTime(row.getValue("Last Activity Date")).format(
                "YYYY-MM-DD"
              )
            : null}
        </div>
      ),
    },
    ...(entityId
      ? [
          {
            header: "Include in Campaign",
            id: "Include in Campaign",
            cell: ({ row }) => {
              const contact = row.original;
              const isIncluded = getIsIncluded(
                includedInCampaign,
                contact,
                campaignId
              );
              return (
                <Switch
                  name="isCampaignTarget"
                  checked={isIncluded}
                  disabled={!canSetCampaignTarget}
                  onCheckedChange={async () => {
                    await handleAddOrRemoveCampaignContact(
                      contact.id,
                      isIncluded,
                      invalidateData
                    );
                  }}
                  aria-label="Include in campaign"
                />
              );
            },
          },
        ]
      : []),
    {
      header: "Status",
      id: "Status",
      cell: ({ row }) => {
        const contact = row.original;
        const isIncluded = getIsIncluded(
          includedInCampaign,
          contact,
          campaignId
        );
        const targets = contact ? contact.campaign_targets : [];
        const campaignTarget =
          targets?.length > 0
            ? targets.filter(c => c.campaign_id === campaignId)[0]
            : null;
        const campaignStatus = campaignTarget
          ? campaignTarget?.campaign_status
          : { id: 0, name: "" };

        return (
          <CampaignStatusModal
            campaignStatus={campaignStatus}
            campaignId={campaignId}
            entityId={entityId || contact?.entity_id}
            contactId={contact?.id}
            updateData={invalidateData}
            entityName={contact?.entity_name}
            disabled={!isIncluded || !canEditStatus}
            effectiveDate={campaignTarget?.effective_date}
          />
        );
      },
    },
    {
      header: <div></div>,
      id: "Actions",
      accessorKey: "actions",
      enableSorting: false,
      enableResizing: false,
      meta: {
        isDisplay: true,
      },
      cell: ({ row }) => {
        const contact = row.original;
        const isIncluded = getIsIncluded(
          includedInCampaign,
          contact,
          campaignId
        );
        const isCallable =
          isIncluded && canCall && !activeSession && isContactCallable(contact);
        return (
          <CampaignContactTableActions
            campaignId={campaignId}
            entityId={entityId}
            contact={contact}
            isIncluded={isIncluded}
            isCallable={isCallable}
          />
        );
      },
    },
  ];
};

export const getCampaignContactsTableFilters = ({
  tags,
  users,
  campaignId,
}) => {
  return [
    {
      key: "last_activity_date",
      title: "Last Activity",
      type: "dateRange",
    },
    {
      key: "effective_date",
      title: "Effective Date",
      type: "dateRange",
    },
    {
      key: "search:contact_name",
      title: "Contact Name",
      type: "input",
    },
    {
      key: "search:contact_email",
      title: "Contact Email",
      type: "input",
    },
    {
      key: "search:contact_phone",
      title: "Contact Phone",
      type: "input",
    },
    {
      key: "department_id",
      title: "Department Name",
      type: "ruvixxSelect",
      queryFn: DepartmentService.getForFilters,
    },
    {
      key: "account_manager_id",
      title: "Account Manager",
      type: "select",
      operators: NULLABLE_OPERATORS,
      data: users,
    },
    {
      key: "assigned_user_id",
      title: "Assignee",
      type: "select",
      operators: NULLABLE_OPERATORS,
      data: users,
    },
    {
      key: "tag_id",
      title: "Tag",
      type: "tag",
      data: tags,
    },
    {
      key: "entity_id",
      title: "Entity",
      type: "ruvixxSelect",
      queryFn: EntityService.getEntitiesForFilters,
      props: {
        campaign_ids: [campaignId],
      },
    },
    {
      title: "Outreach Permissions",
      key: "grid_1",
      type: "grid",
      components: [
        { key: "is_callable", title: "Callable" },
        { key: "not_callable", title: "Not Callable" },
        { key: "is_emailable", title: "Emailable" },
        { key: "not_emailable", title: "Not Emailable" },
      ],
    },
  ];
};
